import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Planet } from '@kaeliux/shared';
import { getTeamPlanet, getTeamPlanetFlights } from '../services/teamPlanet.service';

/** The one cache entry every Team planet reader and writer shares — mutations write their returned planet straight into it. */
export const TEAM_PLANET_QUERY_KEY = ['teamPlanet', 'mine'] as const;

/** Every flight to or from the Team planet — under the `teamPlanet` prefix, so invalidating that refreshes it too. */
export const TEAM_PLANET_FLIGHTS_QUERY_KEY = ['teamPlanet', 'flights'] as const;

interface UseTeamPlanetOptions {
    /** Skip the fetch entirely — e.g. until the viewer's alliance role is known. Defaults to `true`. */
    enabled?: boolean;
    /** Poll on an interval — the Team planet sits outside the app-wide game loop (`useGameLoop` only ever ticks a normal planet), so nothing else keeps it fresh. Omit for a one-shot fetch. */
    refetchInterval?: number;
}

/**
 * The viewer's own alliance's Team planet — `data` is `null` when none has
 * been founded yet. Single shared query key/options so every caller reads
 * the SAME cache entry, and a mutation's
 * `invalidateQueries({ queryKey: ['teamPlanet'] })` refreshes every one of
 * them together.
 */
/**
 * Adopts the Team planet a mutation just returned. Any poll already in
 * flight started before the mutation committed, so it is cancelled first —
 * otherwise its older snapshot could land afterwards and undo the change on
 * screen (queued item gone, resources back), inviting a second queue.
 */
export function useStoreTeamPlanet(): (planet: Planet) => Promise<void> {
    const queryClient = useQueryClient();
    return useCallback(async (planet: Planet) => {
        await queryClient.cancelQueries({ queryKey: TEAM_PLANET_QUERY_KEY });
        queryClient.setQueryData(TEAM_PLANET_QUERY_KEY, planet);
    }, [queryClient]);
}

export function useTeamPlanet({ enabled = true, refetchInterval }: UseTeamPlanetOptions = {}) {
    return useQuery({
        queryKey: TEAM_PLANET_QUERY_KEY,
        queryFn: () => getTeamPlanet(),
        staleTime: 30_000,
        enabled,
        refetchInterval,
    });
}

/** Every member's flights to or from the Team planet (issue #294 flight coordinator). Same options as {@link useTeamPlanet}. */
export function useTeamPlanetFlights({ enabled = true, refetchInterval }: UseTeamPlanetOptions = {}) {
    return useQuery({
        queryKey: TEAM_PLANET_FLIGHTS_QUERY_KEY,
        queryFn: () => getTeamPlanetFlights(),
        staleTime: 10_000,
        enabled,
        refetchInterval,
    });
}
